import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import {
  TrendingUp,
  Users,
  CreditCard,
  DollarSign,
  Calendar,
  RefreshCw,
  Activity,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { colors } from "../../../constants/colors";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { getAnalyticsAction } from "../../../store/slices/analytics/analyticsSliceActions";
import { setSelectedDays } from "../../../store/slices/analytics/analyticsSlice";
import { API_STATUS } from "../../../constants/apiStatus";
import RecentActivitySection from "./RecentActivitySection";

const AdminAnalytics = () => {
  const dispatch = useAppDispatch();

  // Get state from Redux
  const { analytics, selectedDays, error, apiStatus } = useAppSelector(
    (state) => state.analytics
  );

  const isLoading = apiStatus.getAnalytics === API_STATUS.STARTED;

  const dateRangeOptions = [
    { value: 30, label: "30 Days" },
    { value: 60, label: "60 Days" },
    { value: 90, label: "90 Days" },
    { value: 180, label: "180 Days" },
    { value: 360, label: "360 Days" },
  ];

  // Color schemes for charts
  const chartColors = {
    primary: "#3b82f6",
    secondary: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
    purple: "#8b5cf6",
    pink: "#ec4899",
    teal: "#14b8a6",
    orange: "#f97316",
  };

  // Load analytics on component mount and when date range changes
  useEffect(() => {
    dispatch(getAnalyticsAction(selectedDays));
  }, [dispatch, selectedDays]);

  // Handle date range change
  const handleDateRangeChange = (days) => {
    dispatch(setSelectedDays(days));
  };

  // Prepare chart data
  const getUsersChartData = () => {
    if (!analytics) return [];
    return [
      {
        name: "Active Subscriptions",
        value: analytics.users.withActiveSubscription,
        color: chartColors.secondary,
      },
      {
        name: "Expired Subscriptions",
        value: analytics.users.withExpiredSubscription,
        color: chartColors.warning,
      },
      {
        name: "Free Users",
        value: analytics.users.freeUsers,
        color: chartColors.primary,
      },
    ];
  };

  const getSubscriptionChartData = () => {
    if (!analytics) return [];
    return [
      {
        type: "Active",
        Monthly: analytics.subscriptions.active.monthly,
        Yearly: analytics.subscriptions.active.yearly,
      },
      {
        type: "Expired",
        Monthly: analytics.subscriptions.expired.monthly,
        Yearly: analytics.subscriptions.expired.yearly,
      },
    ];
  };

  const getRevenueChartData = () => {
    if (!analytics) return [];
    return [
      {
        name: "Monthly",
        value: analytics.revenue.bySubscriptionType.monthly,
        color: chartColors.primary,
      },
      {
        name: "Yearly",
        value: analytics.revenue.bySubscriptionType.yearly,
        color: chartColors.secondary,
      },
    ];
  };

  const getUsageChartData = () => {
    if (!analytics) return [];
    return [
      {
        name: "Meals",
        Total: analytics.usage.totalMealCount,
        Average: analytics.usage.averageMealCountPerUser,
      },
      {
        name: "Recipes",
        Total: analytics.usage.totalRecipeCount,
        Average: analytics.usage.averageRecipeCountPerUser,
      },
    ];
  };

  // Format currency
  const formatCurrency = (amount, currency = "SEK") => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Styles
  const cardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    marginBottom: "2rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  };

  const headerStyle = {
    fontSize: "2rem",
    fontWeight: "700",
    color: colors.darkBlue,
    marginBottom: "2rem",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  };

  const statCardStyle = {
    backgroundColor: colors.white,
    borderRadius: "12px",
    padding: "1.5rem",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    border: "1px solid #e5e7eb",
  };

  const dateRangeButtonStyle = (isActive) => ({
    padding: "0.5rem 1rem",
    borderRadius: "8px",
    border: `2px solid ${isActive ? chartColors.primary : "#e5e7eb"}`,
    backgroundColor: isActive ? chartColors.primary : "white",
    color: isActive ? "white" : "#374151",
    cursor: "pointer",
    fontWeight: "600",
    fontSize: "0.875rem",
    transition: "all 0.2s",
  });

  if (error) {
    return (
      <div style={cardStyle}>
        <div style={headerStyle}>
          <AlertCircle size={32} color={chartColors.danger} />
          Analytics Dashboard
        </div>
        <div
          style={{
            backgroundColor: "#fee2e2",
            color: "#dc2626",
            padding: "1rem",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          {error}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "2rem",
        backgroundColor: "#f9fafb",
        minHeight: "100vh",
      }}
      className="admin-responsive-container"
    >
      {/* Header */}
      <div style={headerStyle} className="admin-title">
        <TrendingUp size={32} />
        Analytics Dashboard
        {isLoading && <RefreshCw size={24} className="animate-spin" />}
      </div>

      {/* Date Range Selector */}
      <div style={cardStyle} className="admin-card">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1rem",
            flexWrap: "wrap",
          }}
        >
          <Calendar size={20} color={colors.darkBlue} />
          <span style={{ fontWeight: "600", color: colors.darkBlue }}>
            Date Range:
          </span>
        </div>
        <div
          style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}
          className="admin-date-range-buttons"
        >
          {dateRangeOptions.map((option) => (
            <button
              key={option.value}
              style={dateRangeButtonStyle(selectedDays === option.value)}
              onClick={() => handleDateRangeChange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        {analytics && (
          <div
            style={{
              marginTop: "1rem",
              fontSize: "0.875rem",
              color: colors.darkGray,
            }}
          >
            Analyzing data from {analytics.dateRange.startDate} to{" "}
            {analytics.dateRange.endDate}
          </div>
        )}
      </div>

      {isLoading ? (
        <div style={{ ...cardStyle, textAlign: "center", padding: "4rem" }}>
          <RefreshCw
            size={48}
            className="animate-spin"
            color={chartColors.primary}
          />
          <div
            style={{
              marginTop: "1rem",
              fontSize: "1.25rem",
              color: colors.darkGray,
            }}
          >
            Loading analytics data...
          </div>
        </div>
      ) : analytics ? (
        <>
          {/* Key Metrics Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "1.5rem",
              marginBottom: "2rem",
            }}
            className="admin-stat-grid"
          >
            {/* Total Users */}
            <div style={statCardStyle} className="admin-stat-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.25rem",
                    }}
                  >
                    Total Users
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: colors.darkBlue,
                    }}
                  >
                    {analytics.users.total.toLocaleString()}
                  </div>
                </div>
                <Users size={32} color={chartColors.primary} />
              </div>
              <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                <span
                  style={{ color: chartColors.secondary, fontWeight: "600" }}
                >
                  {analytics.users.withActiveSubscription}
                </span>{" "}
                active subscribers
              </div>
            </div>

            {/* Total Revenue */}
            <div style={statCardStyle} className="admin-stat-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.25rem",
                    }}
                  >
                    Revenue ({selectedDays} days)
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: colors.darkBlue,
                    }}
                    className="admin-stat-value"
                  >
                    {formatCurrency(
                      analytics.revenue.totalInDateRange,
                      analytics.revenue.currency
                    )}
                  </div>
                </div>
                <DollarSign size={32} color={chartColors.secondary} />
              </div>
              <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                <span
                  style={{ color: chartColors.secondary, fontWeight: "600" }}
                >
                  {analytics.revenue.transactionCount}
                </span>{" "}
                transactions
              </div>
            </div>

            {/* Active Subscriptions */}
            <div style={statCardStyle} className="admin-stat-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.25rem",
                    }}
                  >
                    Active Subscriptions
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: colors.darkBlue,
                    }}
                    className="admin-stat-value"
                  >
                    {analytics.subscriptions.active.total.toLocaleString()}
                  </div>
                </div>
                <CheckCircle size={32} color={chartColors.secondary} />
              </div>
              <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                <span style={{ color: chartColors.warning, fontWeight: "600" }}>
                  {analytics.subscriptions.expiringInDateRange.total}
                </span>{" "}
                expiring soon
              </div>
            </div>

            {/* Usage Stats */}
            <div style={statCardStyle} className="admin-stat-card">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                      marginBottom: "0.25rem",
                    }}
                  >
                    Total Usage
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: "700",
                      color: colors.darkBlue,
                    }}
                    className="admin-stat-value"
                  >
                    {(
                      analytics.usage.totalMealCount +
                      analytics.usage.totalRecipeCount
                    ).toLocaleString()}
                  </div>
                </div>
                <Activity size={32} color={chartColors.purple} />
              </div>
              <div style={{ fontSize: "0.75rem", color: colors.darkGray }}>
                Meals + Recipes generated
              </div>
            </div>
          </div>

          {/* Charts Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "2rem",
            }}
            className="admin-chart-grid"
          >
            {/* User Distribution Pie Chart */}
            <div style={cardStyle} className="admin-card">
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: colors.darkBlue,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Users size={20} />
                User Distribution
              </h3>
              <ResponsiveContainer
                width="100%"
                height={300}
                className="admin-chart-container"
              >
                <PieChart>
                  <Pie
                    data={getUsersChartData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getUsersChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [value.toLocaleString(), ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Subscription Types Bar Chart */}
            <div style={cardStyle} className="admin-card">
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: colors.darkBlue,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <CreditCard size={20} />
                Subscription Types
              </h3>
              <ResponsiveContainer
                width="100%"
                height={300}
                className="admin-chart-container"
              >
                <BarChart data={getSubscriptionChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="type" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Monthly" fill={chartColors.primary} />
                  <Bar dataKey="Yearly" fill={chartColors.secondary} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Revenue Distribution */}
            <div style={cardStyle} className="admin-card">
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: colors.darkBlue,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <DollarSign size={20} />
                Revenue by Type
              </h3>
              <ResponsiveContainer
                width="100%"
                height={300}
                className="admin-chart-container"
              >
                <PieChart>
                  <Pie
                    data={getRevenueChartData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) =>
                      `${name} ${formatCurrency(
                        value,
                        analytics.revenue.currency
                      )}`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getRevenueChartData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => [
                      formatCurrency(value, analytics.revenue.currency),
                      "",
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Usage Analytics */}
            <div style={cardStyle} className="admin-card">
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: colors.darkBlue,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <Activity size={20} />
                Usage Analytics
              </h3>
              <ResponsiveContainer
                width="100%"
                height={300}
                className="admin-chart-container"
              >
                <BarChart data={getUsageChartData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Total" fill={chartColors.purple} />
                  <Bar dataKey="Average" fill={chartColors.pink} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Expiring Subscriptions Table */}
          {analytics.subscriptions.expiringInDateRange.details.length > 0 && (
            <div style={cardStyle} className="admin-card">
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: colors.darkBlue,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <AlertCircle size={20} />
                Subscriptions Expiring Soon (
                {analytics.subscriptions.expiringInDateRange.total})
              </h3>
              <div
                style={{ overflowX: "auto" }}
                className="admin-table-container"
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    minWidth: "600px",
                  }}
                  className="admin-table"
                >
                  <thead>
                    <tr style={{ backgroundColor: colors.extraLightGray }}>
                      <th
                        style={{
                          padding: "0.75rem",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        User ID
                      </th>
                      <th
                        style={{
                          padding: "0.75rem",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Product
                      </th>
                      <th
                        style={{
                          padding: "0.75rem",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Type
                      </th>
                      <th
                        style={{
                          padding: "0.75rem",
                          textAlign: "left",
                          fontWeight: "600",
                        }}
                      >
                        Expiration Date
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.subscriptions.expiringInDateRange.details
                      .slice(0, 10)
                      .map((sub, index) => (
                        <tr
                          key={index}
                          style={{ borderBottom: "1px solid #e5e7eb" }}
                        >
                          <td
                            style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                          >
                            {sub.userId}
                          </td>
                          <td
                            style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                          >
                            {sub.productName}
                          </td>
                          <td
                            style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                          >
                            <span
                              style={{
                                backgroundColor:
                                  sub.packageType === "MONTHLY"
                                    ? "#dbeafe"
                                    : "#d1fae5",
                                color:
                                  sub.packageType === "MONTHLY"
                                    ? "#1e40af"
                                    : "#065f46",
                                padding: "0.25rem 0.5rem",
                                borderRadius: "4px",
                                fontSize: "0.75rem",
                                fontWeight: "600",
                              }}
                            >
                              {sub.packageType}
                            </span>
                          </td>
                          <td
                            style={{ padding: "0.75rem", fontSize: "0.875rem" }}
                          >
                            {new Date(sub.expirationDate).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {analytics.subscriptions.expiringInDateRange.details.length >
                  10 && (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "1rem",
                      fontSize: "0.875rem",
                      color: colors.darkGray,
                    }}
                  >
                    Showing first 10 of{" "}
                    {analytics.subscriptions.expiringInDateRange.details.length}{" "}
                    expiring subscriptions
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recent Activity Section */}
          <RecentActivitySection />
        </>
      ) : (
        <div style={{ ...cardStyle, textAlign: "center", padding: "4rem" }}>
          <div style={{ fontSize: "1.25rem", color: colors.darkGray }}>
            No analytics data available
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
